import { ArrowRight, RotateCcw } from "lucide-react";

type PlanningProgressRequest = {
  status: string;
  offers?: number | unknown[];
  offerCount?: number;
  pendingApprovalRole?: "SATIN_ALMA_MUDURU" | "GENEL_MUDUR" | null;
};

const steps = [
  "Talep oluşturuldu",
  "Teklifler hazırlanıyor",
  "Onay sürecinde",
  "Tamamlandı",
] as const;

function offerCount(request: PlanningProgressRequest) {
  if (
    typeof request.offerCount === "number" &&
    Number.isFinite(request.offerCount)
  ) {
    return Math.max(0, request.offerCount);
  }

  return Array.isArray(request.offers)
    ? request.offers.length
    : Math.max(0, request.offers ?? 0);
}

export function planningProgressOf(request: PlanningProgressRequest) {
  const isRevision = ["Düzeltme istendi", "Revizyon", "Reddedildi"].includes(
    request.status,
  );
  const offers = offerCount(request);

  if (request.status === "Onaylandı") {
    return {
      currentStep: 4,
      title: "Tamamlandı",
      owner: "Satın alma süreci tamamlandı",
      nextAction: "Sonuç ve karar kayıtları görüntülenebilir",
      isRevision: false,
    };
  }

  if (isRevision) {
    return {
      currentStep: 2,
      title: "Düzeltme bekleniyor",
      owner: "Satın Alma Uzmanında",
      nextAction: "Dosyanın düzeltilip yeniden onaya gönderilmesi bekleniyor",
      isRevision: true,
    };
  }

  if (
    request.pendingApprovalRole ||
    ["Onay bekliyor", "Satın Alma değerlendirmesi bekleniyor"].includes(
      request.status,
    )
  ) {
    const owner =
      request.pendingApprovalRole === "GENEL_MUDUR"
        ? "Genel Müdürde"
        : "Satın Alma Müdüründe";
    return {
      currentStep: 3,
      title: "Onay sürecinde",
      owner,
      nextAction: "Yetkili kararının tamamlanması bekleniyor",
      isRevision: false,
    };
  }

  return {
    currentStep: 2,
    title:
      offers > 0 ? "Teklifler değerlendiriliyor" : "Teklifler hazırlanıyor",
    owner: "Satın Alma Uzmanında",
    nextAction:
      offers > 0
        ? "Teklif seçiminin onaya gönderilmesi bekleniyor"
        : "İlk tedarikçi teklifinin kaydedilmesi bekleniyor",
    isRevision: false,
  };
}

export function PlanningRequestProgress({
  request,
  variant = "compact",
  showNextAction = true,
}: {
  request: PlanningProgressRequest;
  variant?: "compact" | "full";
  showNextAction?: boolean;
}) {
  const progress = planningProgressOf(request);

  return (
    <section
      className={`planning-progress is-${variant}${progress.isRevision ? " is-revision" : ""}`}
      aria-label={`Satın alma süreci: ${progress.title}`}
    >
      {variant === "compact" ? (
        <div className="planning-progress-summary">
          <strong>
            {progress.isRevision ? <RotateCcw size={15} /> : null}
            {progress.title}
          </strong>
          <small>{progress.owner}</small>
        </div>
      ) : null}

      {variant === "full" ? (
        <>
          <header className="planning-progress-full-summary">
            <div>
              <span>Satın alma süreci</span>
              <strong>{progress.title}</strong>
              <small>{progress.currentStep} / 4 aşama tamamlandı</small>
            </div>
            <div className="planning-progress-owner">
              <span>Sorumlu</span>
              <strong>{progress.owner}</strong>
            </div>
          </header>
          <ol
            className="planning-progress-segments"
            aria-label="Süreç ilerlemesi"
          >
            {steps.map((step, index) => {
              const stepNumber = index + 1;
              const completed = stepNumber < progress.currentStep;
              const current = stepNumber === progress.currentStep;
              return (
                <li
                  className={`${completed ? "is-complete" : ""}${current ? " is-current" : ""}`.trim()}
                  key={step}
                  aria-label={`${step}${current ? ", mevcut aşama" : completed ? ", tamamlandı" : ""}`}
                  aria-current={current ? "step" : undefined}
                >
                  <span aria-hidden="true">{completed ? "✓" : stepNumber}</span>
                  <small>{step}</small>
                </li>
              );
            })}
          </ol>
        </>
      ) : null}

      {variant === "full" && showNextAction ? (
        <p className="planning-progress-next">
          <ArrowRight size={18} aria-hidden="true" />
          <span>
            <small>Sıradaki işlem</small>
            <strong>{progress.nextAction}</strong>
          </span>
        </p>
      ) : null}
    </section>
  );
}
